import { useNavigate } from "react-router-dom";
import { useContent } from "../App";
import { Button } from "../components/ui/button";
import { Users, Wifi, Tv, Wind, Tag, Bed, ArrowRight, Info } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const AMENITY_ICONS = { "Free WiFi": Wifi, "WiFi": Wifi, "Smart TV": Tv, "TV": Tv, "AC": Wind, "Work Desk": Tag, "Almira": Tag, "Double Bed": Bed };

export default function RoomsPage() {
  const navigate = useNavigate();
  const { rooms } = useContent();

  usePageSEO({
    title: "Rooms & Prices | Ambe Palace - Best Hotel in Dungarpur, Rajasthan",
    description: "View rooms and prices at Ambe Palace, the best hotel in Dungarpur. Deluxe, Super Deluxe, Executive & Premium Suite rooms starting from INR 849/night with WiFi, TV & AC."
  });

  return (
    <div data-testid="rooms-page">
      {/* Page Header */}
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">Accommodation</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Hotel Rooms in Dungarpur {'\u2014'} Ambe Palace
          </h1>
          <p className="text-base text-slate-400 mt-4 max-w-xl mx-auto">
            Choose from our comfortable hotel rooms in Dungarpur designed for your perfect stay. Book the best rooms at affordable prices.
          </p>
        </div>
      </section>

      {/* Room Cards */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="group bg-white border border-stone-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                data-testid={`room-detail-${room.id}`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="img-zoom aspect-square sm:aspect-auto overflow-hidden">
                  <img src={room.image} alt={`${room.name} at Ambe Palace hotel in Dungarpur Rajasthan`} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {room.name}
                      </h3>
                      <p className="text-amber-600 font-bold text-2xl mb-1">
                        {'\u20B9'}{room.price}
                        <span className="text-xs text-slate-400 font-normal ml-1">/ night</span>
                      </p>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-5">
                        <Users className="w-4 h-4" />
                        <span>{room.capacity} Persons</span>
                      </div>
                      <div className="border-t border-stone-100 pt-4 mb-5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Amenities</p>
                        <div className="flex flex-wrap gap-2">
                          {room.amenities.map((a, i) => {
                            const AIcon = AMENITY_ICONS[a] || Tag;
                            return (
                              <span key={i} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-stone-50 px-2.5 py-1.5 border border-stone-100">
                                <AIcon className="w-3 h-3 text-amber-600" />{a}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  <Button
                    onClick={() => navigate(`/booking?room=${room.id}`)}
                    disabled={room.available === false}
                    className={`w-full text-xs font-bold uppercase tracking-[0.15em] rounded-none h-11 transition-colors duration-200 ${
                      room.available === false
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                    data-testid={`rooms-book-${room.id}-btn`}
                  >
                    {room.available === false ? 'Room Full' : <><span>Book Now</span> <ArrowRight className="w-3.5 h-3.5 ml-2" /></>}
                  </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Extra charges note */}
          <div className="mt-12 bg-amber-50 border-l-4 border-amber-500 p-5" data-testid="extra-charges-note">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-slate-800 mb-1">Additional Charges</p>
                <p className="text-sm text-slate-600">Extra Person Charge: <strong>{'\u20B9'}200</strong> per person per night</p>
                <p className="text-sm text-slate-600">GST: <strong>5%</strong> on total amount</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
